import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface ApiKeyItem {
  id: number;
  name: string;
  keyPrefix: string;
  scopes: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

interface KeysData {
  keys: ApiKeyItem[];
  maxApiKeys: number;
  rateLimitPerMinute: number;
  dailyLimit: number;
}

const SCOPES = [
  { value: "read:conversations", label: "Read conversations" },
  { value: "write:conversations", label: "Create conversations" },
  { value: "read:contacts", label: "Read contacts" },
  { value: "write:contacts", label: "Write contacts" },
];

export default function ApiKeysPanel() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [data, setData] = useState<KeysData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState(["read:conversations"]);
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [revealedKeyId, setRevealedKeyId] = useState<number | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);

  const fetchKeys = useCallback(async () => {
    try {
      const resp = await api.messaging.get("ApiKeys");
      if (resp.ok) setData(await resp.json());
    } catch {
      // Graceful — show empty state
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  async function createKey() {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const resp = await api.messaging.post("ApiKeys", {
        name: newKeyName.trim(),
        scopes: selectedScopes.join(","),
      });
      if (resp.ok) {
        const result = await resp.json();
        setRevealedKey(result.rawKey);
        setRevealedKeyId(result.id);
        setShowCreate(false);
        setNewKeyName("");
        setSelectedScopes(["read:conversations"]);
        await fetchKeys();
      } else {
        const err = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, err.error ?? "Failed to create API key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to create API key.");
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(id: number) {
    try {
      const resp = await api.messaging.delete(`ApiKeys/${id}`);
      if (resp.ok) {
        displayToast(Severity.Success, "API key revoked.");
        setConfirmRevokeId(null);
        await fetchKeys();
      } else {
        displayToast(Severity.Error, "Failed to revoke key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to revoke key.");
    }
  }

  async function rotateKey(id: number, name: string) {
    try {
      const resp = await api.messaging.post(`ApiKeys/${id}/rotate`, {});
      if (resp.ok) {
        const result = await resp.json();
        setRevealedKey(result.rawKey);
        setRevealedKeyId(result.id);
        displayToast(Severity.Success, `"${name}" rotated — copy the new key now.`);
        await fetchKeys();
      } else {
        displayToast(Severity.Error, "Failed to rotate key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to rotate key.");
    }
  }

  function toggleScope(scope: string) {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  }

  function formatDate(iso: string | null) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  const baseUrl = "https://api.velaro.com/v1";

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800">API Keys</h2>
        <p className="text-sm text-slate-500 mt-1">
          Use API keys to authenticate requests to the Velaro REST API.
          Keys are shown once when created — store them securely.
        </p>
      </div>

      {/* Limits banner */}
      {data && (
        <div className="mb-5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex gap-6">
          <span><span className="font-medium">{Array.isArray(data.keys) ? data.keys.length : 0}</span> / {data.maxApiKeys === 0 ? "∞" : data.maxApiKeys} keys</span>
          <span><span className="font-medium">{data.rateLimitPerMinute === 0 ? "∞" : data.rateLimitPerMinute}</span> req/min per key</span>
          <span><span className="font-medium">{data.dailyLimit === 0 ? "∞" : data.dailyLimit.toLocaleString()}</span> req/day site total</span>
        </div>
      )}

      {/* Revealed key banner — shown once */}
      {revealedKey && (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 mb-2">Copy your API key — it won't be shown again</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 text-sm font-mono text-slate-800 overflow-x-auto">
              {revealedKey}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedKey); displayToast(Severity.Success, "Copied!"); }}
              className="shrink-0 px-3 py-2 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-2">
            Use this key in the <code className="font-mono">Authorization: Bearer {"<key>"}</code> header when calling the Velaro API.
          </p>
          <button
            onClick={() => { setRevealedKey(null); setRevealedKeyId(null); }}
            className="mt-2 text-xs text-amber-700 underline"
          >
            I've copied it, dismiss
          </button>
        </div>
      )}

      {/* Key list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : !Array.isArray(data?.keys) || data!.keys.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          No API keys yet. Create one below to get started.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Name</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Prefix</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Scopes</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Created</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Last used</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data!.keys.map((k, i) => (
                <tr key={k.id} className={`border-b border-slate-100 ${revealedKeyId === k.id ? "bg-amber-50" : i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{k.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{k.keyPrefix}…</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{k.scopes.replace(/,/g, ", ")}</td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDate(k.createdAt)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDate(k.lastUsedAt)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2 justify-end">
                      {confirmRevokeId === k.id ? (
                        <>
                          <button onClick={() => revokeKey(k.id)} className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">Confirm revoke</button>
                          <button onClick={() => setConfirmRevokeId(null)} className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => rotateKey(k.id, k.name)} className="text-xs text-slate-500 hover:text-slate-800 underline">Rotate</button>
                          <button onClick={() => setConfirmRevokeId(k.id)} className="text-xs text-red-500 hover:text-red-700 underline">Revoke</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {showCreate ? (
        <div className="border border-slate-200 rounded-lg p-4 mb-5 bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">New API Key</h3>
          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">Key name</label>
            <input
              type="text"
              placeholder="e.g. Production App, Zapier Integration"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              autoFocus
              maxLength={200}
            />
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">Scopes (permissions)</label>
            <div className="space-y-1.5">
              {SCOPES.map(s => (
                <label key={s.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(s.value)}
                    onChange={() => toggleScope(s.value)}
                    className="rounded border-slate-300"
                  />
                  <span>{s.label}</span>
                  <code className="text-xs text-slate-400 font-mono">{s.value}</code>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={createKey}
              disabled={creating || !newKeyName.trim() || selectedScopes.length === 0}
              className="px-4 py-2 text-sm font-medium bg-primary text-white rounded hover:bg-primary-active disabled:opacity-50"
            >
              {creating ? "Creating…" : "Create key"}
            </button>
            <button
              onClick={() => { setShowCreate(false); setNewKeyName(""); setSelectedScopes(["read:conversations"]); }}
              className="px-4 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          disabled={!!(data && data.maxApiKeys > 0 && Array.isArray(data.keys) && data.keys.length >= data.maxApiKeys)}
          className="mb-6 px-4 py-2 text-sm font-medium bg-primary text-white rounded hover:bg-primary-active disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Create API key
        </button>
      )}

      {/* Quick reference */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick reference</h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Base URL</p>
            <code className="block bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-700">
              {baseUrl}
            </code>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Authentication</p>
            <code className="block bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-700">
              Authorization: Bearer vlk_your_api_key_here
            </code>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Example — list conversations</p>
            <code className="block bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-700 whitespace-pre">
{`curl ${baseUrl}/conversations \\
  -H "Authorization: Bearer vlk_your_key"`}
            </code>
          </div>
          <div className="pt-1 border-t border-slate-100 text-xs text-slate-500">
            Rate limits: <strong>{data?.rateLimitPerMinute === 0 ? "unlimited" : (data?.rateLimitPerMinute ?? 60) + " req/min"}</strong> per key.
            For higher limits, contact support.
          </div>
        </div>
      </div>
    </div>
  );
}
